import React from 'react';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Home } from 'lucide-react';
import FacilitySiteTabs from '../FacilitySiteTabs';

const ItemRow = ({ item, index, onChange, testIdPrefix }) => {
  const isManagerModified = item.manager_modified;
  return (
    <div
      key={item.id || index}
      className={`grid grid-cols-[200px_1fr] gap-3 items-start border-b border-slate-100 pb-3 last:border-0 last:pb-0 ${
        isManagerModified ? 'bg-red-50/30 -mx-3 px-3 py-2 rounded' : ''
      }`}
      data-testid={`${testIdPrefix}-${index}`}
    >
      <div className="flex items-center gap-2">
        <Label
          className={`text-sm font-medium ${
            isManagerModified ? 'text-red-600' : 'text-slate-700'
          }`}
        >
          {item.name}
        </Label>
        {isManagerModified && (
          <Badge
            variant="outline"
            className="text-xs py-0 border-red-300 text-red-600 bg-red-50"
          >
            Modified
          </Badge>
        )}
      </div>
      <Textarea
        value={item.value || ''}
        onChange={(e) => onChange(index, 'value', e.target.value)}
        placeholder="Enter your response..."
        className="tactical-input min-h-[60px] text-sm"
        data-testid={`${testIdPrefix}-value-${index}`}
      />
    </div>
  );
};

export const FacilityOverviewSection = ({
  tacticalId,
  facilitySites,
  facilityOverview,
  activeSiteId,
  activeSite,
  hasTah,
  tahLoading,
  isOwner,
  onSiteChange,
  onSitesUpdated,
  onSiteItemChange,
  onLegacyItemChange,
  onTahToggle,
}) => (
  <section>
    <div className="section-header flex items-center justify-between py-1.5 px-3">
      <div className="flex items-center gap-2">
        <span className="text-sm">🏢</span>
        <span className="text-sm">Facility Overview</span>
      </div>
      {/* TAH (Therapy @Home) Toggle */}
      <div className="flex items-center gap-2">
        <div className="flex items-center space-x-2 bg-teal-50 border border-teal-200 rounded-lg px-3 py-1.5">
          <Checkbox
            id="tah-toggle"
            checked={hasTah}
            onCheckedChange={onTahToggle}
            disabled={tahLoading || !isOwner}
            data-testid="tah-checkbox"
          />
          <label
            htmlFor="tah-toggle"
            className="text-sm font-medium text-teal-700 cursor-pointer flex items-center gap-1.5"
          >
            <Home className="w-4 h-4" />
            TAH (Therapy @Home)
          </label>
          {tahLoading && (
            <div className="animate-spin h-4 w-4 border-2 border-teal-500 border-t-transparent rounded-full" />
          )}
        </div>
      </div>
    </div>
    <div className="p-3">
      {/* Site Tabs */}
      {facilitySites.length > 0 && (
        <FacilitySiteTabs
          tacticalId={tacticalId}
          sites={facilitySites}
          activeSiteId={activeSiteId}
          onSiteChange={onSiteChange}
          onSitesUpdated={onSitesUpdated}
          isReadOnly={!isOwner}
        />
      )}

      {/* Show active site items or fallback to legacy facility_overview */}
      {facilitySites.length > 0 && activeSite ? (
        <div className="space-y-3">
          {(activeSite.items || []).map((item, index) => (
            <ItemRow
              key={item.id || index}
              item={item}
              index={index}
              onChange={onSiteItemChange}
              testIdPrefix="site-item"
            />
          ))}
        </div>
      ) : facilityOverview.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-2">
          No facility items. Please create a new tactical to get started.
        </p>
      ) : (
        /* Legacy facility_overview fallback */
        <div className="space-y-3">
          {facilityOverview.map((item, index) => (
            <ItemRow
              key={item.id || index}
              item={item}
              index={index}
              onChange={onLegacyItemChange}
              testIdPrefix="facility-item"
            />
          ))}
        </div>
      )}
    </div>
  </section>
);
